import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ArrowRight, ChartBarDecreasing } from "lucide-react";
import { useMediaQuery } from "@mui/material";

import { UIButton, UICard, UIProgressBar, UITable } from "../../ui";
import DashboardEmptyState from "../DashboardEmptyState";
import EntityCell from "../EntityCell";
import { DEFAULT_RANK_COLOR, RANK_COLOR } from "./constants";
import "../dashboardSection.scss";
import "./TopCompaniesBySaudiApplicants.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");

// Below this width the column minimums no longer fit the card: the number
// columns take a fixed width and Company gets the rest, so nothing is clipped.
const COMPACT_QUERY = "(max-width:599.98px)";

const fixedWidth = (width) => ({
  width,
  minWidth: width,
  maxWidth: width,
  autoFlex: false,
});

/**
 * Admin dashboard "Top Companies by No. of Saudi Applicants" — the top 3
 * companies ranked by Saudi-applicant count, or an empty state (no action —
 * "View Companies" in the header covers it). The Saudi Applicants bar is scaled
 * to the top company and its colour is fixed per rank, not per company.
 */
const TopCompaniesBySaudiApplicants = ({
  title,
  viewCompaniesLabel,
  columns,
  rows,
  emptyTitle,
  emptyDescription,
  onViewCompanies,
  onRowClick,
}) => {
  const compact = useMediaQuery(COMPACT_QUERY);

  const maxSaudiApplicants = useMemo(
    () => Math.max(1, ...rows.map((row) => Number(row.saudiApplicants) || 0)),
    [rows],
  );

  const tableColumns = useMemo(
    () => [
      {
        field: "rank",
        // Empty headerName + autoFlex:false opt out of UITable's auto-sizing
        // (which forces a 130px min); the "#" label comes back via renderHeader.
        // minWidth/maxWidth pin it to 30 — MUI DataGrid's own column default
        // (minWidth: 50) otherwise clamps a bare `width: 30` back up to 50.
        headerName: "",
        renderHeader: () => columns.rank,
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        autoFlex: false,
        headerClassName: "top-companies__rank",
        cellClassName: "top-companies__rank",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <span className="dashboard-rank">{row.rank}</span>
        ),
      },
      {
        field: "company",
        headerName: columns.company,
        flex: compact ? 1 : 1.5,
        minWidth: compact ? 130 : 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <EntityCell
            variant="company"
            name={row.name}
            subtitle={row.activeSince}
            imageUrl={row.imageUrl}
          />
        ),
      },
      {
        field: "saudiApplicants",
        headerName: columns.saudiApplicants,
        ...(compact ? fixedWidth(112) : { flex: 2, minWidth: 150 }),
        headerClassName: "top-companies__saudi-col",
        cellClassName: "top-companies__saudi-col",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <div className="top-companies__saudi">
            <span className="top-companies__count">
              {formatCount(row.saudiApplicants)}
            </span>
            <UIProgressBar
              value={Math.max(
                0,
                Math.min(
                  100,
                  (Number(row.saudiApplicants) / maxSaudiApplicants) * 100,
                ),
              )}
              barColor={RANK_COLOR[row.rank] || DEFAULT_RANK_COLOR}
              size="xs"
              className="top-companies__bar"
            />
          </div>
        ),
      },
      {
        field: "events",
        headerName: columns.events,
        ...(compact ? fixedWidth(60) : { flex: 1, minWidth: 100 }),
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <span className="top-companies__count">
            {formatCount(row.eventsTotal)}
          </span>
        ),
      },
    ],
    [columns, maxSaudiApplicants, compact],
  );

  const viewCompaniesAction = (
    <UIButton
      variant="text"
      endIcon={<ArrowRight size={16} strokeWidth={2} />}
      className="dashboard-link"
      onClick={onViewCompanies}
    >
      {viewCompaniesLabel}
    </UIButton>
  );

  return (
    <UICard
      title={title}
      action={viewCompaniesAction}
      className="dashboard-card top-companies"
    >
      {rows.length > 0 ? (
        <UITable
          className="dashboard-table"
          rows={rows}
          columns={tableColumns}
          contentSized
          hideFooter
          striped={false}
          disableSelectionOnClick
          rowHeight={compact ? 72 : 56}
          onRowClick={(params) => onRowClick(params.row)}
        />
      ) : (
        <DashboardEmptyState
          icon={<ChartBarDecreasing />}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </UICard>
  );
};

TopCompaniesBySaudiApplicants.propTypes = {
  title: PropTypes.string.isRequired,
  viewCompaniesLabel: PropTypes.string.isRequired,
  columns: PropTypes.shape({
    rank: PropTypes.string,
    company: PropTypes.string,
    events: PropTypes.string,
    saudiApplicants: PropTypes.string,
  }).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      rank: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      activeSince: PropTypes.string.isRequired,
      eventsTotal: PropTypes.number.isRequired,
      saudiApplicants: PropTypes.number.isRequired,
    }),
  ),
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  onViewCompanies: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
};

TopCompaniesBySaudiApplicants.defaultProps = {
  rows: [],
};

export default TopCompaniesBySaudiApplicants;
